import { ReactNode, useRef } from 'react'

import { Button } from 'core-components/library/button'
import { Loader } from 'core-components/library/loader'
import { IModalContent } from 'core-components/library/modalDialog'

import { useScreeningRerun } from '../../api/useCompetitorBadActorReview'
import { TScreening, TScreeningRerun } from '../../model/types'
import {
  notScreenedReasonText,
  COMPETITOR_BAD_ACTOR_REVIEW_TEXT as TEXT,
  tierText,
  useReviewText,
} from '../translations'

import { ScreeningMatchesList } from './ScreeningMatchesList'
import { SubmittedValues } from './SubmittedValues'

import styles from './ScreeningRerunDialogContent.module.scss'

interface IScreeningRerunDialogContentProps extends IModalContent {
  requestId: number
  screening: TScreening
  run: number
}

type TScreenedRerun = Extract<TScreeningRerun, { outcome: 'screened' }>

interface IRerunArea {
  status: string
  statusHidden?: true
  details: ReactNode
}

interface IScreenedRerunProps {
  rerun: TScreenedRerun
  listCaption: string | null
}

const ScreenedRerun = ({ rerun, listCaption }: IScreenedRerunProps) => {
  const text = useReviewText()

  return (
    <>
      {listCaption !== null && (
        <ScreeningMatchesList
          comparison="complete"
          entries={rerun.entries}
          caption={listCaption}
          regionLabel={text(TEXT.rerunRegionLabel)}
          oneOpenAtATime
        />
      )}
      <h5>{text(TEXT.rerunSubmittedHeading)}</h5>
      <SubmittedValues submitted={rerun.submitted} />
    </>
  )
}

export const ScreeningRerunDialogContent = ({
  requestId,
  screening,
  run,
}: IScreeningRerunDialogContentProps) => {
  const text = useReviewText()
  const result = useScreeningRerun(requestId, screening, run)
  const rootRef = useRef<HTMLDivElement>(null)

  const retry = () => {
    rootRef.current?.focus()
    result.refetch()
  }

  const screenedArea = (rerun: TScreenedRerun): IRerunArea => {
    const count = rerun.entries.length

    if (count === 0) {
      return {
        status: text(TEXT.rerunNoMatch),
        details: <ScreenedRerun rerun={rerun} listCaption={null} />,
      }
    }

    const tierLabel = tierText(rerun.tier)
    const countText = text(count === 1 ? TEXT.rerunCountOne : TEXT.rerunCountOther, {
      count,
      tier: tierLabel === null ? rerun.tier : text(tierLabel),
    })

    return {
      status: countText,
      statusHidden: true,
      details: <ScreenedRerun rerun={rerun} listCaption={countText} />,
    }
  }

  const rerunArea = (): IRerunArea => {
    if (result.isPending) {
      return { status: text(TEXT.rerunLoading), details: <Loader /> }
    }

    if (result.isError) {
      return {
        status: '',
        details: (
          <>
            <p role="alert" key={result.errorUpdatedAt}>
              {text(TEXT.rerunError)}
            </p>
            <Button variant="outlined" onClick={retry}>
              {text(TEXT.retry)}
            </Button>
          </>
        ),
      }
    }

    switch (result.data.kind) {
      case 'none':
        return { status: '', details: <p role="alert">{text(TEXT.rerunNotFound)}</p> }
      case 'refused':
        return { status: '', details: <p role="alert">{text(TEXT.refused)}</p> }
      case 'answered': {
        const { rerun } = result.data

        return rerun.outcome === 'screened'
          ? screenedArea(rerun)
          : { status: text(notScreenedReasonText(rerun.reason)), details: null }
      }
    }
  }

  const { status, statusHidden, details } = rerunArea()

  return (
    <div ref={rootRef} tabIndex={-1} className={styles.root} aria-busy={result.isPending}>
      <p role="status" className={statusHidden ? styles.visuallyHidden : undefined}>
        {status}
      </p>
      {details}
    </div>
  )
}
